import React from 'react'
import { Heading, Container, Icon } from '@scentregroup/ui'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import DeckScrollWrapper from '../deck-scroll-wrapper'
import ExternalLink from '../external-link'
import analyticsUtils from '../../helpers/analytics-utils'
import {
  TABINDEX_TABBABLE,
  TABINDEX_NOT_TABBABLE,
} from '../../helpers/a11y-helpers'
import { getClientConfigValue } from '../../helpers/config-helper'
import { STACKLA_PROXY } from '../../constants'
import withTouchDetection from '../../helpers/with-touch-detection'
import lazyloadWrapper from '../../helpers/lazyload-wrapper'
import style from './index.scss'

class SocialFeed extends React.Component {
  constructor(props) {
    super(props)
    this.entryIds = props.entries.map(entry => entry.id)

    this.state = {
      // The feed item currently receiving keyboard focus
      focusedEntry: this.entryIds[0] || null,
      // The feed item currently displaying text details (can be different due to mouse hover)
      activeEntry: null,
      // Images that failed to load, for reporting back to Stackla
      brokenEntries: [],
    }
    this.gridNode = React.createRef()
  }

  componentDidMount() {
    this.trackModuleImpression()
  }

  trackModuleImpression = () => {
    analyticsUtils.gaTagTracking({
      event: analyticsUtils.events.MODULE_IMPRESSION,
      eventCategory: analyticsUtils.categories.USER_GENERATED_CONTENT,
      eventAction: analyticsUtils.actions.INSTA_DECK_SHOW,
    })
  }

  trackModuleClick = () => {
    analyticsUtils.gaTagTracking({
      event: analyticsUtils.events.USER_CLICK,
      eventCategory: analyticsUtils.categories.USER_GENERATED_CONTENT,
      eventAction: analyticsUtils.actions.INSTA_DECK_TILE,
    })
  }

  showDetails = id => {
    this.setState({ activeEntry: id })
  }

  hideDetails = () => {
    this.setState({ activeEntry: null })
  }

  focusDetails = id => {
    this.setState({ focusedEntry: id, activeEntry: id })
    const index = this.entryIds.indexOf(id)
    const elem = this.gridNode.current.children[index]
    if (elem && elem.focus) {
      elem.focus()
    }
  }

  keyPressed = event => {
    const { focusedEntry } = this.state
    const entryIds = this.entryIds
    const focusedIndex = entryIds.indexOf(focusedEntry)

    const focusOn = index => {
      this.focusDetails(entryIds[index])
      event.preventDefault()
    }

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        if (focusedIndex > 0) {
          focusOn(focusedIndex - 1)
        }
        break
      case 'ArrowRight':
      case 'ArrowDown':
        if (focusedIndex < entryIds.length - 1) {
          focusOn(focusedIndex + 1)
        }
        break
      case 'Home':
        focusOn(0)
        break
      case 'End':
        focusOn(entryIds.length - 1)
        break
      default:
        break
    }
  }

  itemFocused = id => {
    if (this.props.isUsingTouch && id !== this.state.activeEntry) {
      this.trackModuleClick()
    }
    this.setState({ focusedEntry: id, activeEntry: id })
  }

  // Handle re-opening a still-focused entry on iOS Safari
  itemClicked = id => {
    if (this.props.isUsingTouch && id !== this.state.activeEntry) {
      this.itemFocused(id)
    }
  }

  gridBlurred = () => {
    // setTimeout is required to make sure the DOM has registered focus on a new element first
    setTimeout(() => {
      if (this.gridNode.current) {
        const hasInnerFocus = this.gridNode.current.contains(
          document.activeElement
        )
        if (!hasInnerFocus) {
          this.hideDetails()
        }
      }
    }, 0)
  }

  entryFail = entry => {
    const rawScentreApi = getClientConfigValue('CONTENT_SERVICE').replace(
      /\/graphql(\/)?$/,
      ''
    )
    this.setState({ brokenEntries: [...this.state.brokenEntries, entry.id] })
    fetch(
      `${rawScentreApi}/${STACKLA_PROXY}_id=${entry.id}&stack=${
        this.props.stackShortName
      }`
    )
  }

  render() {
    const SOCIAL_FEED_LENGTH = 20 // Evenly divisible by 4 and 5 for different layouts
    const { color, title, isUsingTouch } = this.props
    const { focusedEntry, activeEntry, brokenEntries } = this.state

    const visibleEntries = this.props.entries
      .filter(e => !brokenEntries.includes(e.id))
      .slice(0, SOCIAL_FEED_LENGTH)
    this.entryIds = visibleEntries.map(entry => entry.id)
    return (
      <Container type="large" className={style.socialDeckContainer}>
        <Heading type="large" className={style.socialDeckHeading}>
          {title}
        </Heading>
        <DeckScrollWrapper
          className={style.socialDeckWrapper}
          eventCategory={analyticsUtils.categories.USER_GENERATED_CONTENT}
          eventAction={analyticsUtils.actions.INSTA_DECK_SCROLL}
          bgColor={color}
        >
          <div role="grid" aria-label="Social media images">
            <div
              className={style.socialDeck}
              role="row"
              ref={this.gridNode}
              onBlur={this.gridBlurred}
            >
              {visibleEntries.map(entry => (
                <div
                  key={entry.id}
                  className={classNames(style.deckItem, {
                    [style.deckItemActive]: activeEntry === entry.id,
                  })}
                  role="gridcell"
                  tabIndex={
                    focusedEntry === entry.id
                      ? TABINDEX_TABBABLE
                      : TABINDEX_NOT_TABBABLE
                  }
                  onFocus={() => this.itemFocused(entry.id)}
                  onClick={() => this.itemClicked(entry.id)}
                  onKeyDown={this.keyPressed}
                  onMouseEnter={() => {
                    if (!isUsingTouch) {
                      this.showDetails(entry.id)
                    }
                  }}
                  onMouseLeave={() => {
                    if (!isUsingTouch) {
                      this.hideDetails()
                    }
                  }}
                >
                  <figure>
                    <figcaption className={style.deckInner}>
                      <div className={style.deckText}>
                        {entry.username && (
                          <div className={style.deckHeading}>
                            @{entry.username}
                          </div>
                        )}
                        <div className={style.deckCaption}>{entry.caption}</div>
                        <ExternalLink
                          url={entry.url}
                          className={style.deckLink}
                          eventCategory={
                            analyticsUtils.categories.USER_GENERATED_CONTENT
                          }
                          eventAction={analyticsUtils.actions.INSTA_DECK_LINK}
                        >
                          View on Instagram
                          <Icon
                            className={style.linkIcon}
                            size="small"
                            type="arrow-right"
                          />
                        </ExternalLink>
                      </div>
                    </figcaption>
                    <img
                      alt={entry.title || entry.caption}
                      src={entry.image.url}
                      onError={() => this.entryFail(entry)}
                    />
                  </figure>
                </div>
              ))}
            </div>
          </div>
        </DeckScrollWrapper>
      </Container>
    )
  }
}

SocialFeed.propTypes = {
  isUsingTouch: PropTypes.bool,
  color: PropTypes.string,
  title: PropTypes.string,
  stackShortName: PropTypes.string,
  entries: PropTypes.array,
}

export const RawSocialFeed = SocialFeed

export default lazyloadWrapper(withTouchDetection(RawSocialFeed))
